import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ButtonBack } from '../../../../components/ButtonBack';
import { Card } from '../../../../components/Card';
import { List, ListItem } from '../../../../components/List';
import { Loading } from '../../../../components/Loading';
import { HOME, KNOWLEDGE_TRAIL, QUIZ } from '../../../../constants/Routes';
import { useAuth } from '../../../../hooks/useAuth';
import { finishKnowledgeTrail } from '../../api';
import { IKnowledgeTrail } from '../../types';
import { CertificateModal } from '../CertificateModal/CertificateModal';
import { Collapsible } from '../Collapsible/Collapsible';
import { KnowledgeTrailOnboarding } from '../KnowledgeTrailOnboarding/KnowledgeTrailOnboarding';
import { ModalConfirmFinishTrail } from '../ModalConfirmFinishTrail';
import { ModalRateTrail } from '../ModalRateTrail';
import styles from './KnowledgeTrailDetail.module.css';
import { Notify } from '../../../../components/Notify';

interface KnowledgeTrailDetailProps {
  trail: IKnowledgeTrail;
  onRefresh?: () => Promise<void>;
}

export function KnowledgeTrailDetail({
  trail,
  onRefresh,
}: KnowledgeTrailDetailProps) {
  const navigate = useNavigate();
  const { player, client } = useAuth();
  const [isFinishing, setIsFinishing] = useState(false);
  const [showCertificate, setShowCertificate] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isRateModalOpen, setIsRateModalOpen] = useState(false);

  if (!trail) {
    return <Loading />;
  }

  const { settings, content, completion } = trail;

  const answeredCount = content.filter(
    (item) => item.playerPerformance?.nOfAttempts > 0
  ).length;
  const notAnsweredCount = content.length - answeredCount;
  const currentPerformance =
    content.length > 0
      ? Math.round(
          content.reduce(
            (sum, item) =>
              sum +
              (item.playerPerformance?.percentageCorrectInBestAttempt ?? 0),
            0
          ) / content.length
        )
      : 0;
  const isExpired = settings.endingDate
    ? Date.now() > settings.endingDate
    : false;
  const canFinish =
    notAnsweredCount === 0 &&
    !isExpired &&
    completion?.completedAt === undefined;

  async function handleFinishTrail() {
    if (!player?.id) return;
    setIsFinishing(true);
    try {
      await finishKnowledgeTrail(trail.id, player.id);
      Notify('Trilha finalizada com sucesso!', 'success');
      setIsRateModalOpen(true);
      if (onRefresh) await onRefresh();
    } catch (error) {
      Notify(
        'Erro ao finalizar a trilha. Por favor, tente novamente.',
        'error'
      );
    } finally {
      setIsFinishing(false);
    }
  }

  return (
    <>
      <KnowledgeTrailOnboarding
        trailId={trail.id}
        trailTitle={trail.title}
        onClose={() => {}}
      />
      <div className={`container ${styles.container}`}>
        <div className="my-3">
          <ButtonBack title="Home" to={HOME} />
        </div>

        <div className="row">
          <div className="col-12 col-md-7">
            <div
              className={styles.banner}
              style={
                !trail.coverImage
                  ? { backgroundColor: client?.primaryColor || '#6b7280' }
                  : undefined
              }
            >
              {trail.coverImage ? (
                <img src={trail.coverImage} alt={trail.title} />
              ) : (
                <span className={styles.bannerTitle}>{trail.title}</span>
              )}
            </div>

            <h1 className={styles.title}>{trail.title}</h1>
            <p className={styles.description}>{trail.description}</p>

            {trail.courseBookUrl && (
              <a
                href={trail.courseBookUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.courseBook}
              >
                <i className="material-icons-outlined">download</i>
                <span>Clique aqui para baixar o guia geral da Trilha</span>
              </a>
            )}

            <h2 className={styles.sectionTitle}>Conteúdos</h2>
            {content.map((item, index) => (
              <Collapsible key={item.quiz?.id ?? index} title={item.title}>
                <p className={styles.description}>{item.description}</p>
                <div className={styles.contentStats}>
                  {item.quiz && (
                    <>
                      <span>
                        Número de questões: {item.quiz.questions?.length ?? 0}
                      </span>
                      <span>
                        Total de tentativas:{' '}
                        {item.quiz.quizDetails?.amountOfRetries ?? 0}
                      </span>
                      <span>
                        Tentativas restantes:{' '}
                        {item.quiz.quizDetails?.amountOfRetries -
                          item.playerPerformance.nOfAttempts}
                      </span>
                      <span>
                        Porcentagem de acerto na melhor tentativa:{' '}
                        {item.playerPerformance
                          .percentageCorrectInBestAttempt ?? '0'}
                        %
                      </span>
                    </>
                  )}
                </div>
                <div className={styles.contentActions}>
                  <button
                    onClick={() =>
                      navigate(
                        `${KNOWLEDGE_TRAIL}/${trail.id}/content/${index}`,
                        { state: { trail } }
                      )
                    }
                  >
                    Acessar conteúdo
                  </button>
                  <button
                    onClick={() =>
                      navigate(`${QUIZ}/${item.quiz.id}`, {
                        state: {
                          returnPath: `${KNOWLEDGE_TRAIL}/${trail.id}`,
                          returnState: { trail },
                        },
                      })
                    }
                    disabled={
                      !item.quiz ||
                      !item.playerPerformance.isPlayerAllowedToAnswerQuiz ||
                      completion?.completedAt !== undefined
                    }
                  >
                    Responder agora
                  </button>
                </div>
              </Collapsible>
            ))}

            <button
              className={styles.finishButton}
              disabled={!canFinish || isFinishing}
              onClick={() => setIsConfirmModalOpen(true)}
            >
              {isFinishing ? 'Finalizando...' : 'Finalizar trilha'}
            </button>
          </div>

          {/* Right column — Sidebar */}
          <div className="col-12 col-md-5">
            <div className="my-3 my-md-0">
              <Card>
                <span className="fw-semibold">Detalhes</span>
                <hr />

                <div className="my-3">
                  <div className="mt-3">
                    <p>Você pontuou até agora:</p>
                    <span
                      className={`badge bg-secondary w-100 center-text fs-5 ${
                        (completion?.pointsAwarded ?? 0) > 0
                          ? 'bg-success'
                          : 'bg-danger'
                      }`}
                    >
                      {completion?.pointsAwarded ?? 0}{' '}
                      {client?.coinName
                        ? client.coinName.replace(
                            /\((\w+)\)/,
                            (_, conteudo) => conteudo
                          )
                        : 'pontos'}
                    </span>
                  </div>
                </div>
                <span className={styles.progressTitle}>Progresso</span>
                <div className={styles.progressBar}>
                  {content.map((item, i) => (
                    <div
                      key={i}
                      className={`${styles.progressSegment} ${
                        item.playerPerformance?.nOfAttempts > 0
                          ? styles.answered
                          : ''
                      }`}
                    />
                  ))}
                </div>
                <div className={styles.progressLabel}>
                  {answeredCount}/{content.length}
                </div>
                <List>
                  <ListItem
                    icon="assignment_turned_in"
                    styleIcon="outlined"
                    content="Pontos ao finalizar a trilha"
                    secondaryContent={`${settings.rewardPoints} pontos`}
                  />
                  <ListItem
                    icon="live_help"
                    styleIcon="outlined"
                    content="Tipo de avaliação"
                    secondaryContent="Performance"
                  />
                  <ListItem
                    icon="format_list_numbered"
                    styleIcon="outlined"
                    content="Número de conteúdos"
                    secondaryContent={content.length}
                  />
                  <ListItem
                    icon="flag"
                    styleIcon="outlined"
                    content="Desempenho mínimo"
                    secondaryContent={`${settings.minimumScore}%`}
                  />
                  <ListItem
                    icon="trending_up"
                    styleIcon="outlined"
                    content="Desempenho atual"
                    secondaryContent={`${currentPerformance}%`}
                  />
                  <ListItem
                    icon="redo"
                    styleIcon="outlined"
                    content="Tentativas possíveis por conteúdo"
                    secondaryContent={settings.amountOfRetries}
                  />
                  {settings.endingDate && (
                    <ListItem
                      icon="pending_actions"
                      styleIcon="outlined"
                      content="Encerramento"
                      secondaryContent={new Date(
                        settings.endingDate
                      ).toLocaleDateString('pt-BR')}
                    />
                  )}
                </List>

                {trail.certificate?.enabled &&
                  completion?.passed &&
                  completion?.certificateUrl && (
                    <button
                      className={styles.certificateBtn}
                      onClick={() => setShowCertificate(true)}
                    >
                      <i className="material-icons-outlined">
                        workspace_premium
                      </i>
                      Ver Certificado
                    </button>
                  )}
              </Card>
            </div>
          </div>
        </div>
      </div>

      {showCertificate && completion?.certificateUrl && (
        <CertificateModal
          certificateUrl={completion.certificateUrl}
          onClose={() => setShowCertificate(false)}
        />
      )}

      <ModalConfirmFinishTrail
        modalIsOpen={isConfirmModalOpen}
        toggleModal={() => setIsConfirmModalOpen(false)}
        onConfirm={handleFinishTrail}
      />

      {player?.id && (
        <ModalRateTrail
          modalIsOpen={isRateModalOpen}
          toggleModal={() => setIsRateModalOpen(false)}
          trailId={trail.id}
          playerId={player.id}
        />
      )}
    </>
  );
}
